import { useCallback } from "react";

import { useSafeLocalStorage } from "@/hooks/useSafeLocalStorage";

/** The History pane's toggles, as this browser keeps them. */
export type ThreadFilterToggles = {
  /** A demo control only: it switches and is saved, but the thread shows the same either way. */
  showSystemMessages: boolean;
  /** Shows the TMS event pills in the thread. */
  showTmsEvents: boolean;
};

/**
 * The one key both toggles are saved under. Not the earlier "hide" toggles' key, whose saved `false` would now read as
 * hiding.
 */
const THREAD_FILTERS_KEY = "dispatchThreadShown";

const SHOW_EVERYTHING: ThreadFilterToggles = { showSystemMessages: true, showTmsEvents: true };

/**
 * The History pane's toggles, saved in this browser so they follow the dispatcher from driver to driver and survive a
 * reload; both start on.
 *
 * @returns The toggles, and a setter that changes one of them.
 */
export function useThreadFilters(): [ThreadFilterToggles, (toggle: keyof ThreadFilterToggles, value: boolean) => void] {
  const [stored, setStored] = useSafeLocalStorage<ThreadFilterToggles>(THREAD_FILTERS_KEY, SHOW_EVERYTHING);
  // A value saved before a toggle existed reads that toggle as on.
  const filters = { ...SHOW_EVERYTHING, ...stored };
  const setToggle = useCallback(
    (toggle: keyof ThreadFilterToggles, value: boolean) =>
      setStored((previous) => ({ ...SHOW_EVERYTHING, ...previous, [toggle]: value })),
    [setStored],
  );
  return [filters, setToggle];
}
